import { createContext, useContext, type ReactNode } from 'react';

import { BFG_MODEL_CONFIG, INCUMBENT_MODEL_CONFIG } from '@/features/forecasts/config/fctRuns';
import { useFctForecasts, type FctForecast } from '@/features/forecasts/hooks/useFctForecasts';

export type FctModelForecasts = { series: FctForecast[]; isLoading: boolean; isError: boolean };

type FctForecastsContextValue = {
  bfg: FctModelForecasts;
  incumbent: FctModelForecasts;
};

const FctForecastsContext = createContext<FctForecastsContextValue | null>(null);

// Fetches BFG and ETS forecasts once for the whole forecast-details panel. All
// pills (BFG, Incumbent, Comparison) read from here, so the underlying
// solutions/run/model-outputs calls are issued a single time and reused.
export function FctForecastsProvider({ children }: { children: ReactNode }) {
  const bfg = useFctForecasts(BFG_MODEL_CONFIG);
  const incumbent = useFctForecasts(INCUMBENT_MODEL_CONFIG);

  const hasError = bfg.isError || incumbent.isError;

  return (
    <FctForecastsContext.Provider
      value={{
        bfg: { series: bfg.series, isLoading: bfg.isLoading, isError: bfg.isError },
        incumbent: { series: incumbent.series, isLoading: incumbent.isLoading, isError: incumbent.isError },
      }}
    >
      {bfg.probes}
      {incumbent.probes}
      {hasError ? (
        <div className="flex items-center justify-center py-12 text-sm text-gray-500">
          Failed to load forecast data. Please try again.
        </div>
      ) : (
        children
      )}
    </FctForecastsContext.Provider>
  );
}

export function useFctForecastsContext(): FctForecastsContextValue {
  const ctx = useContext(FctForecastsContext);
  if (!ctx) throw new Error('useFctForecastsContext must be used within FctForecastsProvider');
  return ctx;
}
